import { useState, useEffect } from 'react'
import { Link } from 'react-router-dom'
import { apiClient, SalaryList, User } from '../lib/api'
import { useLiff } from '../hooks/useLiff'

export default function SalaryAdminPage() {
  const { accessToken, isReady } = useLiff()
  const [user, setUser] = useState<User | null>(null)
  const [userLoading, setUserLoading] = useState(true)
  const [salaryList, setSalaryList] = useState<SalaryList | null>(null)
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)
  const [selectedMonth, setSelectedMonth] = useState('')
  const [downloadingCsv, setDownloadingCsv] = useState(false)

  // 現在の月をデフォルトに設定
  const getCurrentMonth = () => {
    const now = new Date()
    return `${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, '0')}`
  }

  // 月のオプション生成（過去6ヶ月分）
  const generateMonthOptions = () => {
    const options = []
    const now = new Date()
    
    for (let i = 0; i < 6; i++) {
      const date = new Date(now.getFullYear(), now.getMonth() - i, 1)
      const monthStr = `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, '0')}`
      const displayStr = date.toLocaleDateString('ja-JP', { year: 'numeric', month: 'long' })
      options.push({ value: monthStr, label: displayStr })
    }
    
    return options
  }

  const loadSalaryList = async (month: string) => {
    try {
      setLoading(true)
      setError(null)
      const response = await apiClient.getAllSalary(month)
      if (response.success) {
        setSalaryList(response.salaryList)
      } else {
        throw new Error(response.error || '給与データの取得に失敗しました')
      }
    } catch (err) {
      setError(err instanceof Error ? err.message : 'エラーが発生しました')
      setSalaryList(null)
    } finally {
      setLoading(false)
    }
  }

  const downloadCsv = async () => {
    if (!selectedMonth) return

    try {
      setDownloadingCsv(true)
      setError(null)
      const response = await apiClient.downloadSalaryCsv(selectedMonth)
      
      if (response.ok) {
        const blob = await response.blob()
        const url = window.URL.createObjectURL(blob)
        const a = document.createElement('a')
        a.href = url
        a.download = `salary_${selectedMonth}.csv`
        document.body.appendChild(a)
        a.click()
        document.body.removeChild(a)
        window.URL.revokeObjectURL(url)
      } else {
        throw new Error('CSVダウンロードに失敗しました')
      }
    } catch (err) {
      setError(err instanceof Error ? err.message : 'エラーが発生しました')
    } finally {
      setDownloadingCsv(false)
    }
  }

  const formatCurrency = (amount: number) => {
    return amount.toLocaleString('ja-JP') + '円'
  }

  useEffect(() => {
    const currentMonth = getCurrentMonth()
    setSelectedMonth(currentMonth)
  }, [])

  const loadUser = async () => {
    try {
      setUserLoading(true)
      const response = await apiClient.getMe()
      setUser(response.user)
    } catch (error) {
      console.error('Failed to load user:', error)
    } finally {
      setUserLoading(false)
    }
  }

  useEffect(() => {
    if (isReady && accessToken) {
      apiClient.setAccessToken(accessToken)
      loadUser()
    }
  }, [isReady, accessToken])

  useEffect(() => {
    if (user && selectedMonth) {
      if (user.role !== 'admin') {
        setError('管理者権限が必要です')
        return
      }
      loadSalaryList(selectedMonth)
    }
  }, [user, selectedMonth])

  if (!isReady || userLoading) {
    return (
      <div className="min-h-screen bg-gradient-to-b from-purple-50 to-white flex items-center justify-center">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-purple-500"></div>
      </div>
    )
  }

  if (user?.role !== 'admin') {
    return (
      <div className="min-h-screen bg-gradient-to-b from-purple-50 to-white">
        <header className="bg-white shadow-sm border-b border-gray-200">
          <div className="flex items-center justify-between px-4 py-4">
            <div className="flex items-center space-x-3">
              <Link to="/" className="text-purple-500 text-xl">←</Link>
              <div>
                <h1 className="text-lg font-bold text-gray-900">給与管理</h1>
              </div>
            </div>
          </div>
        </header>
        
        <main className="p-4">
          <div className="bg-red-50 border border-red-200 rounded-lg p-8 text-center">
            <div className="text-4xl mb-4">🚫</div>
            <h2 className="text-xl font-bold text-gray-900 mb-2">アクセス権限がありません</h2>
            <p className="text-gray-600">
              この画面は管理者のみ閲覧できます
            </p>
          </div>
        </main>
      </div>
    )
  }

  return (
    <div className="min-h-screen bg-gradient-to-b from-purple-50 to-white">
      {/* ヘッダー */}
      <header className="bg-white shadow-sm border-b border-gray-200">
        <div className="flex items-center justify-between px-4 py-4">
          <div className="flex items-center space-x-3">
            <Link to="/" className="text-purple-500 text-xl">←</Link>
            <div>
              <h1 className="text-lg font-bold text-gray-900">給与管理</h1>
            </div>
          </div>
        </div>
      </header>

      <main className="p-4 pb-20">
        {/* 月選択とCSVダウンロード */}
        <div className="bg-white rounded-xl shadow-lg border border-gray-100 p-6 mb-6">
          <div className="flex items-center justify-between mb-4">
            <label htmlFor="month" className="block text-sm font-medium text-gray-700">
              対象月を選択
            </label>
            <button
              onClick={downloadCsv}
              disabled={!selectedMonth || downloadingCsv}
              className="bg-green-500 text-white px-4 py-2 rounded-lg text-sm font-medium hover:bg-green-600 disabled:opacity-50"
            >
              {downloadingCsv ? 'ダウンロード中...' : '📊 CSV出力'}
            </button>
          </div>
          
          <select
            id="month"
            value={selectedMonth}
            onChange={(e) => setSelectedMonth(e.target.value)}
            className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-purple-500"
          >
            <option value="">月を選択してください</option>
            {generateMonthOptions().map((option) => (
              <option key={option.value} value={option.value}>
                {option.label}
              </option>
            ))}
          </select>
        </div>

        {error && (
          <div className="bg-red-50 border border-red-200 rounded-lg p-4 mb-6">
            <p className="text-red-600 text-sm">{error}</p>
          </div>
        )}

        {loading ? (
          <div className="flex justify-center">
            <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-purple-500"></div>
          </div>
        ) : salaryList ? (
          <div className="space-y-6">
            {/* 合計金額サマリー */}
            <div className="bg-white rounded-xl shadow-lg border border-gray-100 p-6">
              <div className="flex items-center mb-4">
                <div className="text-2xl mr-3">💼</div>
                <h2 className="text-xl font-bold text-gray-900">給与合計</h2>
              </div>
              
              <div className="text-center">
                <div className="text-sm text-gray-600 mb-1">全スタッフ合計支給額</div>
                <div className="text-4xl font-bold text-purple-600">
                  {formatCurrency(salaryList.grandTotal)}
                </div>
              </div>
              
              <div className="mt-4 text-center text-sm text-gray-500">
                {salaryList.employees.length}人のスタッフ
              </div>
            </div>

            {/* スタッフ別給与一覧 */}
            <div className="bg-white rounded-xl shadow-lg border border-gray-100 p-6">
              <div className="flex items-center mb-4">
                <div className="text-2xl mr-3">👥</div>
                <h2 className="text-xl font-bold text-gray-900">スタッフ別明細</h2>
              </div>

              {salaryList.employees.length === 0 ? (
                <div className="text-center py-8">
                  <div className="text-4xl mb-4">😴</div>
                  <p className="text-gray-600">この月の給与データがありません</p>
                </div>
              ) : (
                <div className="space-y-4">
                  {salaryList.employees
                    .sort((a, b) => b.totalAmount - a.totalAmount) // 給与額でソート
                    .map((employee) => (
                      <div
                        key={employee.userId}
                        className="flex items-center justify-between p-4 bg-gray-50 rounded-lg border border-gray-100"
                      >
                        <div>
                          <div className="font-semibold text-gray-900 mb-1">
                            {employee.name}
                          </div>
                          <div className="text-sm text-gray-600">
                            時給: {formatCurrency(employee.hourlyWage)} × {employee.totalWorkHours}時間
                          </div>
                        </div>
                        <div className="text-right">
                          <div className="text-xl font-bold text-gray-900">
                            {formatCurrency(employee.totalAmount)}
                          </div>
                          <div className="text-sm text-gray-600">
                            {employee.totalWorkHours}h
                          </div>
                        </div>
                      </div>
                    ))}
                </div>
              )}
            </div>

            {/* 統計情報 */}
            {salaryList.employees.length > 0 && (
              <div className="bg-white rounded-xl shadow-lg border border-gray-100 p-6">
                <div className="flex items-center mb-4">
                  <div className="text-2xl mr-3">📊</div>
                  <h2 className="text-xl font-bold text-gray-900">統計</h2>
                </div>

                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <div className="text-sm text-gray-600">平均給与</div>
                    <div className="text-lg font-semibold text-gray-900">
                      {formatCurrency(Math.round(salaryList.grandTotal / salaryList.employees.length))}
                    </div>
                  </div>
                  <div>
                    <div className="text-sm text-gray-600">平均勤務時間</div>
                    <div className="text-lg font-semibold text-gray-900">
                      {Math.round(
                        (salaryList.employees.reduce((sum, emp) => sum + emp.totalWorkHours, 0) / salaryList.employees.length) * 10
                      ) / 10}時間
                    </div>
                  </div>
                </div>
              </div>
            )}
          </div>
        ) : selectedMonth && (
          <div className="bg-white rounded-xl shadow-lg border border-gray-100 p-8 text-center">
            <div className="text-4xl mb-4">🔍</div>
            <h2 className="text-xl font-bold text-gray-900 mb-2">データがありません</h2>
            <p className="text-gray-600">
              選択された月の給与データが見つかりませんでした
            </p>
          </div>
        )}
      </main>
    </div>
  )
}